import type {
  AvailabilityDay,
  ClinicianAvailabilityRange,
} from "@/types/clinician";

export const DAYS: { value: AvailabilityDay; label: string }[] = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

export const MAX_RANGES_PER_DAY = 6;

export const AVAILABILITY_TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];

export interface TimeRange {
  start: string;
  end: string;
}

/** Ranges for each day of the week, keyed by AvailabilityDay. */
export type WeeklyHours = Record<AvailabilityDay, TimeRange[]>;

/** A range may end at 23:59: it runs to midnight. */
export const END_OF_DAY = "23:59";

/** Every quarter hour of the day, "00:00" … "23:45". */
export const QUARTER_HOURS: string[] = Array.from({ length: 96 }, (_, index) => {
  const hours = String(Math.floor(index / 4)).padStart(2, "0");
  const minutes = String((index % 4) * 15).padStart(2, "0");
  return `${hours}:${minutes}`;
});

export function minutesOf(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function emptyWeek(): WeeklyHours {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

export function weekFromRanges(ranges: ClinicianAvailabilityRange[]): WeeklyHours {
  const week = emptyWeek();
  for (const range of ranges) {
    week[range.day_of_week].push({ start: range.start, end: range.end });
  }
  for (const day of DAYS) {
    week[day.value].sort((a, b) => (minutesOf(a.start) ?? 0) - (minutesOf(b.start) ?? 0));
  }
  return week;
}

/** The PUT body's weekly list: every range tagged with its day, in order. */
export function weeklyPayload(week: WeeklyHours): ClinicianAvailabilityRange[] {
  return DAYS.flatMap((day) =>
    [...week[day.value]]
      .sort((a, b) => (minutesOf(a.start) ?? 0) - (minutesOf(b.start) ?? 0))
      .map((range) => ({ day_of_week: day.value, start: range.start, end: range.end })),
  );
}

/** What's wrong with each day's hours; an empty result means it can be saved. */
export function validateWeek(week: WeeklyHours): Partial<Record<AvailabilityDay, string>> {
  const problems: Partial<Record<AvailabilityDay, string>> = {};
  for (const day of DAYS) {
    const ranges = week[day.value];
    if (ranges.length > MAX_RANGES_PER_DAY) {
      problems[day.value] = `At most ${MAX_RANGES_PER_DAY} time ranges a day.`;
      continue;
    }
    const spans: [number, number][] = [];
    for (const range of ranges) {
      const start = minutesOf(range.start);
      const end = minutesOf(range.end);
      const endsAtMidnight = range.end === END_OF_DAY;
      if (start === null || end === null || start % 15 !== 0 || (end % 15 !== 0 && !endsAtMidnight)) {
        problems[day.value] = "Times must be on the quarter hour.";
        break;
      }
      if (end <= start) {
        problems[day.value] = "Each range must end after it starts.";
        break;
      }
      spans.push([start, end]);
    }
    if (problems[day.value]) continue;
    spans.sort((a, b) => a[0] - b[0]);
    for (let index = 1; index < spans.length; index += 1) {
      if (spans[index][0] < spans[index - 1][1]) {
        problems[day.value] = "Time ranges overlap.";
        break;
      }
    }
  }
  return problems;
}

/** Monday's hours repeated Tuesday–Friday; the weekend is left alone. */
export function copyMondayToWeekdays(week: WeeklyHours): WeeklyHours {
  const monday = week[0].map((range) => ({ ...range }));
  return {
    ...week,
    1: monday.map((range) => ({ ...range })),
    2: monday.map((range) => ({ ...range })),
    3: monday.map((range) => ({ ...range })),
    4: monday.map((range) => ({ ...range })),
  };
}

function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second"),
  );
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * A wall-clock "YYYY-MM-DDTHH:MM" in ``timeZone`` as an ISO instant, so time
 * off entered for the provider's calendar means the same moment wherever the
 * person editing it is.
 */
export function wallTimeToIso(local: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  let offset = zoneOffsetMinutes(new Date(guess), timeZone);
  // Re-check at the corrected instant so a DST change between the guess and
  // the real time doesn't shift the result by an hour.
  offset = zoneOffsetMinutes(new Date(guess - offset * 60_000), timeZone);
  const instant = new Date(guess - offset * 60_000);
  // A time the clocks skip (spring forward, e.g. 2:30 am) has no instant:
  // converting it would silently block a different hour. Refuse it.
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );
  const roundTrip = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  return roundTrip === local ? instant.toISOString() : null;
}

export function formatInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatTime(time: string): string {
  if (time === END_OF_DAY) return "midnight";
  const minutes = minutesOf(time);
  if (minutes === null) return time;
  const hours = Math.floor(minutes / 60);
  const suffix = hours < 12 ? "am" : "pm";
  const display = hours % 12 === 0 ? 12 : hours % 12;
  const rest = minutes % 60;
  return rest ? `${display}:${String(rest).padStart(2, "0")}${suffix}` : `${display}${suffix}`;
}

type AvailabilityErrorLike = {
  status?: number;
  code?: string;
  message?: string;
  payload?: Record<string, unknown>;
} | null | undefined;

/** A plain sentence for an availability or time-off failure. */
export function availabilityErrorMessage(error: AvailabilityErrorLike, fallback: string): string {
  if (!error) return fallback;
  const serverMessage = error.message?.trim();
  switch (error.code) {
    case "availability_time_off_unavailable":
      return "Time off can't be set for this calendar yet.";
    case "time_off_not_found":
      return "That time off is no longer on this calendar.";
    case "time_off_not_removable":
      return "This block was made in Healthie; remove it there.";
    case "availability_forbidden":
      return "Only a clinical supervisor can change another clinician's hours.";
    case "availability_healthie_unavailable":
      return "Healthie couldn't be reached — try again.";
    case "availability_invalid":
    case "time_off_invalid":
      return serverMessage || fallback;
    case "availability_write_failed": {
      const applied = error.payload?.applied as
        | { created?: number; deleted?: number; planned_created?: number; planned_deleted?: number }
        | undefined;
      const planned = (applied?.planned_created ?? 0) + (applied?.planned_deleted ?? 0);
      const done = (applied?.created ?? 0) + (applied?.deleted ?? 0);
      const progress = applied && planned ? ` (${done} of ${planned} changes went through)` : "";
      return `Healthie didn't take every change${progress}. What's shown is what Healthie has now; check it and save again.`;
    }
    default:
      return serverMessage || fallback;
  }
}
